import React, { Activity, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import { CustomText } from '@/components/CustomText';
import { Button, Panel } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { AppText95 } from '@/components/AppText95';
import { AppContext } from '@/components/AppContext';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import { Link } from 'expo-router';
import Footer from '@/components/Footer';
import { Community, UNKNOWN_COMMUNITY } from "@/types/community";
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { RelativePathString, useRouter } from 'expo-router';
import * as AppConsts from '@/utilities/AppConsts';
import { RabblSearchResult } from "@/components/RabblSearchResult";
import { updateVisuals } from '@/state/uploads';
import { CommunityMembership } from '@/types/community_membership';
import { CustomDivider } from '@/components/CustomDivider';
import { BulletList } from '@/components/BulletList';
import { CustomAccordian, AccordianSection } from '@/components/CustomAccordian';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  image: {
    width: '100%',
    height: 200,
  },
  centerAndPadding: {
    padding: 15,
    alignItems: 'center',
  },
  windowView: {
    alignItems: 'center',
  },
  horizontal: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  contentWrapper: {
    padding: 20,
    gap: 20,
  },
  contentWrapper2: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    paddingTop: 5,
    gap: 10,
    width: '100%',
  },
  black: {
    color: '#000000',
  },
  divider1: {
    marginTop: 15,
    marginBottom: 10,
  },
  padding5: {
    padding: 5,
  },
  bold: {
    fontWeight: "bold",
  },
  strikethrough: {
    textDecorationLine: 'line-through',
  },
  empty: {
  },
  creatorsWrapper: {
    gap: 5,
    padding: 20,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  bulletWrapper: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  bulletsWrapper: {
    padding: 10,
    gap: 15,
  },
  faqsWrapper: {
    padding: 10,
  },
});

interface DashboardProps {
}

const Dashboard: React.FC<DashboardProps> = ({
}) => {
  const faqData = new Map<string, AccordianSection>([
    [
      "Does it cost anything to create a Rabbl?",
      {
        content: (
          <>
            <AppText>Nope</AppText>
          </>
        )
      }
    ],
    [
      "Why is there a cover charge for members?",
      {
        content: (
          <View style={AppConstants.STYLES.gap20}>
            <AppText>First, you can make it free for up to the first {AppConsts.MAX_FREE_MEMBERSHIPS} people. Otherwise, the cover charge reduces fake profiles, scammers, helps fund Rabbl, and you get a cut which incentivizes you to market it and bring in more people.</AppText>
            <AppText>We show prospective members how many people are in the Rabbl to help them decide if they want to join (including the break down of men and women).</AppText>
            <AppText>If they join and don't like anyone, they've got a lifetime membership to this Rabbl. They won't even have to check the app because we'll optionally email them when new people join.</AppText>
          </View>
        )
      }
    ],
    [
      "What's the minimum cover charge?",
      {
        content: (
          <>
            <AppText>${AppConsts.MIN_COVER_CHARGE} but you can also make it free for up to the first {AppConsts.MAX_FREE_MEMBERSHIPS} people.</AppText>
          </>
        )
      }
    ],
    [
      "Can there be different charges for men and women?",
      {
        content: (
          <>
            <AppText>Yes</AppText>
          </>
        )
      }
    ],
    [
      "How do I get paid?",
      {
        content: (
          <>
            <AppText>We're still working on the details and options, but at first we'll just send you a check every month.</AppText>
          </>
        )
      }
    ],
    [
      "Will my new Rabbl be visible immediately?",
      {
        content: (
          <>
            <AppText>No, we manually approve every new Rabbl.</AppText>
          </>
        )
      }
    ],
  ]);

  return (
    <View style={styles.creatorsWrapper}>

      <View style={AppConstants.STYLES.alignCenter}>
        <CustomText>For your members</CustomText>
      </View>

      <View style={AppConstants.STYLES.padding10} />

      <BulletList
        data={[
          { content: "Unlimited free likes", bold: true },
          { content: "Unlimited free messages", bold: true },
          { content: "Email, app and/or text notifications" },
          { content: "Optional notifications when people join" },
          { content: AppUtils.getLifetimeMembershipText(undefined) },
        ]}
      />

      <CustomDivider fullWidth={true} margin={true} />

      <View style={AppConstants.STYLES.alignCenter}>
        <CustomText>For you</CustomText>
      </View>

      <View style={AppConstants.STYLES.padding5} />

      <BulletList
        data={AppUtils.CREATOR_BULLETS_BOLD}
        bulletStart={5}
      />

      <CustomDivider fullWidth={true} margin={true} />

      <View style={AppConstants.STYLES.alignCenter}>
        <CustomText>Frequently Asked Questions</CustomText>
      </View>

      <CustomAccordian data={faqData} maxWidth={400} />

      <View style={AppConstants.STYLES.padding2} />
    </View>
  );
};

interface Feature {
  name: string;
  highlight?: boolean;
}

function CreateBox() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const userInfo = useSelector((state: RootState) => state.user.user_info);

  const onSubmit = useCallback(async () => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "CreateRabblForm.onSubmit",
        "/rabbl/api/createrabbl",
        "POST",
        {
        }
      );

      const current_community: Community = ((response as any).body as any).community;
      dispatch(setData(response));
      dispatch(updateVisuals([]));
      router.replace(AppUtils.getRabblCreateNextPageLink(current_community, userInfo) as RelativePathString);
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "CreateRabblForm.onSubmit");
    } finally {
      hideSpinner();
    }
  }, []);

  return (
    <Button onPress={onSubmit}>Create a Rabbl</Button>
  );
}

interface RabblsYoureInProps {
  communityMemberships: CommunityMembership[],
}

function RabblsYoureIn({communityMemberships} : RabblsYoureInProps) {
  return (
    <Panel variant='raised' elevation={4}>
      <View style={AppConstants.STYLES.padding20gap20center}>
        <CustomText>Rabbls you're in</CustomText>
        {
          communityMemberships && communityMemberships.length > 0 ?
              <>
                {communityMemberships.map((communityMembership, index) =>
                  <View key={communityMembership.uniqueid} style={{ width: "100%", alignItems: "center" }}>
                    <View style={AppConstants.STYLES.padding5} />
                    <CustomDivider fullWidth={true} marginBottom={true} />
                    <View style={AppConstants.STYLES.padding5} />
                    <RabblSearchResult community={communityMembership.community ?? UNKNOWN_COMMUNITY} community_membership={communityMembership} />
                  </View>
                )}
              </>
            :
              <>
                <AppText>You're not a member of any Rabbls yet.</AppText>
                <Link href="/findrabbl" asChild className={"nontext"}><Button>Search Rabbls</Button></Link>
              </>
        }
      </View>
    </Panel>
  );
}

interface RabblsYouOwnProps {
  owned_communities: Community[],
}

function RabblsYouOwn({owned_communities} : RabblsYouOwnProps) {
  return (
    <Panel variant='raised' elevation={4}>
      <View style={AppConstants.STYLES.padding20gap20center}>
        <CustomText>Rabbls you own</CustomText>
        {
          owned_communities && owned_communities.length > 0 ?
              <>
                {owned_communities.map((community, index) =>
                  <View key={community.id} style={{ width: "100%", alignItems: "center" }}>
                    <CustomDivider fullWidth={true} marginBottom={true} />
                    <RabblSearchResult community={community} admin={true} />
                  </View>
                )}
              </>
            :
              <>
                <AppText>You haven't created any Rabbls of your own yet.</AppText>
                <CreateBox />
              </>
        }
      </View>
    </Panel>
  );
}

export default function Rabbls() {
  const owned_communities = useSelector((state: RootState) => state.user.owned_communities);
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const communityMemberships = useSelector((state: RootState) => state.user.communityMemberships);

  if (AppUtilities.DEBUGLEVEL.dev) {
    AppUtilities.info("Rabbls render loggedIn: " + loggedIn);
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Your Rabbls"} />

      { loggedIn &&
          (
            (owned_communities && owned_communities.length > 0) || !communityMemberships || communityMemberships.length == 0 ?
                <>
                  <RabblsYouOwn owned_communities={owned_communities} />
                  <View style={AppConstants.STYLES.padding5} />
                  <RabblsYoureIn communityMemberships={communityMemberships} />
                  <View style={AppConstants.STYLES.padding5} />
                  <CustomDivider fullWidth={true} />
                </>
              :
                <>
                  <RabblsYoureIn communityMemberships={communityMemberships} />
                  <View style={AppConstants.STYLES.padding5} />
                  <RabblsYouOwn owned_communities={owned_communities} />
                  <View style={AppConstants.STYLES.padding5} />
                  <CustomDivider fullWidth={true} />
                </>
          )
      }

      <View style={AppConstants.STYLES.padding2} />

      <CustomText>Create a Rabbl</CustomText>

      <Panel variant='raised' elevation={4}>
        <Dashboard />
      </Panel>
      
      <View style={AppConstants.STYLES.padding5} />

      <CustomDivider fullWidth={true} />

      <View style={AppConstants.STYLES.padding2} />

      <Panel variant='raised' elevation={4}>
        <View style={AppConstants.STYLES.padding20gap20center}>
          <CustomText>Create a Rabbl</CustomText>

          <AppText>Ready to create your own Rabbl for free?</AppText>

          {
            loggedIn ?
                (
                  <>
                    <CreateBox />
                  </>
                )
              :
                (
                  <>
                    <Link href="/signup" asChild className={"nontext"}><Button size="lg"><AppText95 style={styles.bold}>Create account</AppText95></Button></Link>
                    <Link href="/signin" asChild className={"nontext"}><Button size="lg">{AppConsts.EXISING_USERS_LOGIN}</Button></Link>
                  </>
                )
          }
          
        </View>
      </Panel>

      <Footer />
    </BasicScrollView>
  );
}
